import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Popconfirm, PopconfirmProps } from 'antd';
import React, { useContext, useEffect } from 'react';
import { useMutation } from 'react-relay';

import DeleteListItemMutation from '../../graphql/mutations/DeleteListItemMutation';
import useCommitItem from '../../hooks/useCommitItem';
import useDispatchSetterStore from '../../hooks/useDispatchSetterStore';
import useDynamicSelector from '../../hooks/useDynamicSelector';
import { SmxPropsContext } from '../../providers/propsProvider';
import { getSetterListItem, IListItemState } from '../../store/slices/listItemSlice';
import { ListEditModeType, ListItemType } from '../../types';

const styleButton: React.CSSProperties = {
    fontSize: 12,
    padding: 3,
    margin: 0,
    borderRadius: 10,
    border: '1px solid grey',
};

interface IListItemEditButton {
    item: ListItemType;
    connectionId: string;
}

const ListItemEditButton: React.FC<IListItemEditButton> = ({ item, connectionId }) => {
    const { nameList, prefixStore } = useContext(SmxPropsContext);
    const setterListItem = useDispatchSetterStore<IListItemState>(getSetterListItem(nameList + prefixStore));
    const [commitDelete, isInFlightDelete] = useMutation(DeleteListItemMutation);
    const { getCommitItemDelete } = useCommitItem();
    const editMode = useDynamicSelector('listItem', 'editMode');
    const currentEditItem = useDynamicSelector('listItem', 'currentEditItem');
    const typeList = useDynamicSelector('listItem', 'typeList');

    const onClickEdit = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
        const newEditMode: ListEditModeType =
            editMode === 'viewing' || currentEditItem?.id !== item.id ? 'editing' : 'viewing';
        setterListItem({
            currentEditItem: newEditMode !== 'viewing' ? item : null,
            editMode: newEditMode,
        });
    };

    useEffect(() => {
        setterListItem({ spinner: isInFlightDelete });
    }, [isInFlightDelete]);

    const onClickDelete: PopconfirmProps['onConfirm'] = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e?.stopPropagation();

        (async () => {
            try {
                if (typeList === 'remote') {
                    commitDelete(getCommitItemDelete(item.id, connectionId));
                }
            } catch (err) {
                console.log(err);
            }
        })();
    };

    const renderButton = (icon: React.ReactNode, danger: boolean = false) => {
        const button = (
            <Button type="text" shape="circle" size={'small'} style={{ padding: 2 }} danger={danger}>
                {icon}
            </Button>
        );
        return danger ? (
            <span
                onClick={() => {
                    setterListItem({ editMode: 'deleting' });
                }}
            >
                {button}
            </span>
        ) : (
            button
        );
    };

    const iconDelete = (
        <CloseOutlined
            style={{
                ...styleButton,
                border: '1px solid red',
                color: 'red',
            }}
        />
    );

    const iconEdit = (
        <EditOutlined
            style={{
                ...styleButton,
            }}
            onClick={onClickEdit}
        />
    );

    return (
        <div
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            {renderButton(iconEdit)}
            <Popconfirm
                title="Delete the item"
                description="Are you sure to delete this item?"
                onConfirm={onClickDelete}
                okText="Yes"
                cancelText="No"
            >
                {renderButton(iconDelete, true)}
            </Popconfirm>
        </div>
    );
};

export default ListItemEditButton;
